import { create } from 'zustand';
import { setToLocalStorage } from '../config/helpers/local-storage-helper';
import {
  Login,
  ResetPassword,
  Signup,
  UpdatePassword,
  UpdateProfile,
  VerificationCode,
  VerifyAccount,
  getAffiliateStats,
  getReferredUsers,
  getUserActivities,
} from '../service/affiliate-user';

export const useAffiliateUserStore = create((set, get) => ({
  isAuthenticated: false,
  user: null,
  loading: false,
  referredUsers: [],
  dashboardStats: null,
  userActivities: {},

  signup: async (payload) => {
    try {
      set({ loading: true });
      const response = await Signup(payload);
      set({ loading: false });
      return response;
    } catch (error) {
      set({ loading: false });
      console.log(error);
      throw error;
    }
  },

  login: async (payload) => {
    try {
      set({ loading: true });
      const response = await Login(payload);

      if (response?.user && response?.access_token) {
        const { user, access_token } = response;

        // Store authentication data
        setToLocalStorage({
          affiliateUserId: user.id,
          affiliateAccessToken: access_token,
          affiliateUserData: JSON.stringify(user),
        });

        set({
          isAuthenticated: true,
          user: user,
          loading: false,
        });

        return { success: true, user };
      }

      set({ loading: false });
      return { success: false, message: 'Login failed' };
    } catch (error) {
      set({ loading: false });
      console.log('Login error:', error);
      throw error;
    }
  },

  verifyAccount: async (payload) => {
    try {
      set({ loading: true });
      const response = await VerifyAccount(payload);

      // Handle multiple possible response formats
      if (response?.user && response?.access_token) {
        const { user, access_token } = response;

        setToLocalStorage({
          affiliateUserId: user.id,
          affiliateAccessToken: access_token,
          affiliateUserData: JSON.stringify(user),
        });

        set({
          isAuthenticated: true,
          user: user,
          loading: false,
        });

        return { success: true, user };
      } else if (
        response?.success &&
        response?.data?.user &&
        response?.data?.access_token
      ) {
        // Handle nested data format
        const { user, access_token } = response.data;

        setToLocalStorage({
          affiliateUserId: user.id,
          affiliateAccessToken: access_token,
          affiliateUserData: JSON.stringify(user),
        });

        set({
          isAuthenticated: true,
          user: user,
          loading: false,
        });

        return { success: true, user };
      } else if (response?.success) {
        // Handle success without user data
        set({ loading: false });
        return { success: true };
      }

      set({ loading: false });
      return { success: false, message: 'Verification failed' };
    } catch (error) {
      set({ loading: false });
      console.log('VerifyAccount error:', error);
      // Don't re-throw to prevent double error handling
      return {
        success: false,
        error: error?.response?.data?.message || 'Verification failed',
      };
    }
  },

  verificationCode: async (payload) => {
    try {
      set({ loading: true });
      const response = await VerificationCode(payload);
      set({ loading: false });
      return response;
    } catch (error) {
      set({ loading: false });
      console.log(error);
      throw error;
    }
  },

  updatePassword: async (payload) => {
    try {
      set({ loading: true });
      const response = await UpdatePassword(payload);
      set({ loading: false });
      return response;
    } catch (error) {
      set({ loading: false });
      console.log(error);
      throw error;
    }
  },

  resetPassword: async (payload) => {
    try {
      set({ loading: true });
      const response = await ResetPassword(payload);
      set({ loading: false });
      return response;
    } catch (error) {
      set({ loading: false });
      console.log(error);
      throw error;
    }
  },

  updateProfile: async (payload) => {
    try {
      set({ loading: true });
      console.log(payload);
      const response = await UpdateProfile(payload);

      if (response?.user) {
        const updatedUser = response.user;

        // Update localStorage
        setToLocalStorage({
          affiliateUserData: JSON.stringify(updatedUser),
        });

        set({
          user: updatedUser,
          loading: false,
        });

        return { success: true, user: updatedUser };
      }

      set({ loading: false });
      return response;
    } catch (error) {
      set({ loading: false });
      console.log(error);
      throw error;
    }
  },

  logout: () => {
    set({
      isAuthenticated: false,
      user: null,
      loading: false,
    });

    // Clear local storage
    localStorage.removeItem('affiliateUserId');
    localStorage.removeItem('affiliateAccessToken');
    localStorage.removeItem('affiliateUserData');
  },

  // Initialize auth state from localStorage
  initializeAuth: () => {
    try {
      const affiliateUserId = localStorage.getItem('affiliateUserId');
      const affiliateAccessToken = localStorage.getItem('affiliateAccessToken');
      const affiliateUserData = localStorage.getItem('affiliateUserData');

      if (affiliateUserId && affiliateAccessToken && affiliateUserData) {
        const parsedUser = JSON.parse(affiliateUserData);

        set({
          isAuthenticated: true,
          user: parsedUser,
        });
      } else {
        set({
          isAuthenticated: false,
          user: null,
        });
      }
    } catch (error) {
      console.log('Error initializing auth:', error);
      // Clear corrupted data
      localStorage.removeItem('affiliateUserId');
      localStorage.removeItem('affiliateAccessToken');
      localStorage.removeItem('affiliateUserData');

      set({
        isAuthenticated: false,
        user: null,
      });
    }
  },

  // Fetch referred users for dashboard
  fetchReferredUsers: async () => {
    try {
      set({ loading: true });
      const response = await getReferredUsers();

      if (response.success) {
        set({
          referredUsers: response.data || [],
          loading: false,
        });
        return response;
      } else {
        set({ loading: false });
        throw new Error(response.message || 'Failed to fetch referred users');
      }
    } catch (error) {
      set({ loading: false });
      console.error('Fetch referred users error:', error);
      throw error;
    }
  },

  // Fetch dashboard stats
  fetchDashboardStats: async () => {
    try {
      set({ loading: true });
      const response = await getAffiliateStats();

      if (response.success) {
        set({
          dashboardStats: response.data,
          loading: false,
        });
        return response;
      } else {
        set({ loading: false });
        throw new Error(response.message || 'Failed to fetch dashboard stats');
      }
    } catch (error) {
      set({ loading: false });
      console.error('Fetch dashboard stats error:', error);
      throw error;
    }
  },

  // Fetch user activities
  fetchUserActivities: async (userId) => {
    try {
      const response = await getUserActivities(userId);

      if (response.success) {
        set((state) => ({
          userActivities: {
            ...state.userActivities,
            [userId]: response.data || [],
          },
        }));
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch user activities');
      }
    } catch (error) {
      console.error('Fetch user activities error:', error);
      throw error;
    }
  },

  // Clear dashboard data on logout
  clearDashboardData: () => {
    set({
      referredUsers: [],
      dashboardStats: null,
      userActivities: {},
    });
  },
}));
